import { CalendlyService } from './../../services/calendly/calendly.service';
import { MessageService } from 'primeng/api';
import { Component, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { MentorService } from '../../services/account/mentor.service';
import { StudentMentorResponse } from '../../models/account/mentor/studentMentorResponse';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PeerReviewService } from '../../services/peer-review/peer-review.service';
import { PeerReviewerResponse } from '../../models/super-admin/peerReviewerResponse';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ContactPurpose } from '../../models/enums/contactUs';
import { ClarityCallPopupComponent } from '../../shared/components/clarity-call-popup/clarity-call-popup.component';

@Component({
  selector: 'app-peer',
  standalone: true,
  imports: [ButtonModule, SkeletonModule, CommonModule, RouterModule, FormsModule, DialogModule, ClarityCallPopupComponent],
  templateUrl: './peer.component.html',
  styleUrl: './peer.component.scss'
})
export class PeerComponent implements OnInit {
  isLoadingPeerReviewer: boolean = false;
  pageSize = 8; // Fits 8 peer cards per page so slot 3 can contain the CTA card (3x3 grid)
  totalRecords: number = 0;
  peerReviewers: PeerReviewerResponse[] = [];

  // Filter properties
  selectedCountry: string = 'All Countries';
  selectedBoard: string = 'All Boards';
  selectedTarget: string = 'All peers';
  searchTerm: string = '';
  currentPage: number = 1;

  // Clarity Call dialog state
  showClarityCallDialog: boolean = false;
  ContactPurpose = ContactPurpose;

  // Counts collection
  allPeersForCounts: PeerReviewerResponse[] = [];

  get totalPeersCount(): number {
    return this.allPeersForCounts.length;
  }

  getInitialsDot(firstName?: string | null, lastName?: string | null): string {
    const first = firstName ? firstName.trim().charAt(0) : '';
    const last = lastName ? lastName.trim().charAt(0) : '';
    if (first && last) return `${first.toUpperCase()}.${last.toUpperCase()}.`;
    if (first) return `${first.toUpperCase()}.`;
    return 'Peer';
  }

  getCountryCode(countryName: string): string {
    if (!countryName) return 'in';
    const name = countryName.toLowerCase().trim();
    if (name === 'united states' || name === 'us' || name === 'usa' || name === 'u.s.a' || name === 'u.s.') return 'us';
    if (name === 'united kingdom' || name === 'uk' || name === 'u.k.' || name === 'england' || name === 'great britain' || name === 'gb') return 'gb';
    if (name === 'canada') return 'ca';
    if (name === 'australia') return 'au';
    if (name === 'germany') return 'de';
    if (name === 'singapore') return 'sg';
    if (name === 'india') return 'in';
    
    const popular: { [key: string]: string } = {
      'france': 'fr', 'italy': 'it', 'spain': 'es', 'netherlands': 'nl',
      'switzerland': 'ch', 'sweden': 'se', 'ireland': 'ie', 'new zealand': 'nz',
      'china': 'cn', 'japan': 'jp', 'south korea': 'kr', 'hong kong': 'hk'
    };
    return popular[name] || 'in';
  }

  getCardDisplayData(item: PeerReviewerResponse): any {
    const ugName = item.underGraduateCollageName?.trim() || '';
    const ugCountry = item.underGraduateCollageCountry?.trim() || '';
    const ugMajor = item.underGraduateMajorOrDegree?.trim() || '';

    const gradName = item.graduateCollageName?.trim() || '';
    const gradCountry = item.graduateCollageCountry?.trim() || '';
    const gradMajor = item.graduateMajorOrDegree?.trim() || '';

    const isUgIndia = ugCountry.toLowerCase() === 'india' || (!ugCountry && !!ugName);
    const isUgAbroad = ugCountry && ugCountry.toLowerCase() !== 'india';

    const hasGrad = !!gradName;
    const isGradIndia = hasGrad && (gradCountry.toLowerCase() === 'india' || !gradCountry);
    const isGradAbroad = hasGrad && gradCountry && gradCountry.toLowerCase() !== 'india';

    let tag = 'Undergrad';
    let flagCountry = 'india';
    let uniName = ugName;
    let major = ugMajor;

    if (hasGrad) {
      if (isUgIndia && isGradIndia) {
        tag = 'Masters';
        flagCountry = 'india';
        uniName = gradName;
        major = gradMajor;
      } else if (isUgIndia && isGradAbroad) {
        tag = 'Masters';
        flagCountry = gradCountry;
        uniName = gradName;
        major = gradMajor;
      } else if (isUgAbroad && isGradAbroad) {
        tag = 'Undergrad & Masters';
        flagCountry = gradCountry;
        uniName = `${ugName} | ${gradName}`;
        major = `${ugMajor} | ${gradMajor}`;
      } else {
        tag = 'Masters';
        flagCountry = gradCountry || 'india';
        uniName = gradName;
        major = gradMajor;
      }
    } else {
      if (isUgAbroad) {
        tag = 'Undergrad';
        flagCountry = ugCountry;
        uniName = ugName;
        major = ugMajor;
      } else {
        tag = 'Undergrad';
        flagCountry = 'india';
        uniName = ugName;
        major = ugMajor;
      }
    }

    if (item.highestLevelEducation?.toLowerCase() === 'ph.d' || item.highestLevelEducation?.toLowerCase() === 'phd') {
      tag = 'PhD';
    }

    const code = this.getCountryCode(flagCountry);
    const flagUrl = `https://flagcdn.com/w40/${code}.png`;

    return {
      tag,
      flagUrl,
      uniName,
      major
    };
  }

  get totalUniversitiesCount(): number {
    const unis = new Set<string>();
    this.allPeersForCounts.forEach(p => {
      if (p.underGraduateCollageName && p.underGraduateCollageName.trim() !== '') {
        unis.add(p.underGraduateCollageName.trim().toLowerCase());
      }
      if (p.graduateCollageName && p.graduateCollageName.trim() !== '') {
        unis.add(p.graduateCollageName.trim().toLowerCase());
      }
    });
    return unis.size;
  }

  get totalCountriesCount(): number {
    const countries = new Set<string>();
    this.allPeersForCounts.forEach(p => {
      if (p.country && p.country.trim() !== '') {
        countries.add(p.country.trim().toLowerCase());
      }
    });
    return countries.size;
  }

  // FAQ Accordion Index
  activeFaqIndex: number | null = null;

  faqs = [
    {
      question: 'What exactly is a Peer, and how are they different from a counsellor?',
      answer: 'Peers are current students or recent alumni at universities abroad — they\'ve been through the exact process you\'re navigating. Unlike counsellors, they give you the unfiltered, lived experience: what a campus is really like, how their workload felt, what they wish they\'d known before applying. Think of them as a knowledgeable friend, not an advisor.'
    },
    {
      question: 'What can I ask my Peer about?',
      answer: 'Quite a lot — Peers can walk you through university selection, application strategy, and the admissions process, as well as the everyday realities of student life: housing, costs, safety, and settling into a new culture. They\'ll share their own journey, the decisions they made, and what they\'d do differently. Many Peers are also open to sharing details about their academic profile — test scores, work experience, universities they applied to, and even their essays or SOP if they\'re comfortable doing so.'
    },
    {
      question: 'Is talking to a Peer free?',
      answer: 'Peer sessions are available for a small nominal fee — this keeps things fair and respectful of everyone\'s time, including yours. You get a focused 20-minute call where you can come prepared with your questions and make the most of the conversation.'
    },
    {
      question: 'How do I choose the right Peer for me?',
      answer: 'Filter by university, course, country, or background. If you\'re applying to LSE for Economics or NUS for Public Policy, look for a Peer who studied exactly that — they\'ll have the most relevant insight. You can read their profile and pick who feels like the best fit before booking.'
    },
    {
      question: 'What happens in a session? Do I need to prepare anything?',
      answer: 'Sessions are 20 minutes, so it helps to come with a few specific questions ready — about the city, the programme, student life, or how your Peer handled the application. The conversation is relaxed, not formal, but a little preparation goes a long way in making the most of your time together.'
    },
    {
      question: 'Can I talk to more than one Peer?',
      answer: 'Absolutely — and we\'d encourage it. If you\'re shortlisting between two universities or two countries, talking to Peers from each can make that decision a lot clearer. Different perspectives help.'
    },
    {
      question: 'Are Peers honest, or do they just say good things about their university?',
      answer: 'Peers are encouraged to give you the real picture — the good and the difficult. We want you to make an informed choice, not rosy one. Most Peers will tell you what surprised them, what they\'d do differently, and what to genuinely watch out for.'
    },
    {
      question: 'What if I have questions after the session?',
      answer: 'You can always book another session or follow up through the platform. For document-specific queries or application strategy, your GradPath counsellor is your go-to — Peers and counsellors work together to make sure nothing falls through the cracks.'
    }
  ];

  constructor(
    private messageService: MessageService,
    private router: Router,
    private peerReviewService: PeerReviewService,
  ) {}

  ngOnInit(): void {
    this.getPeers(1);
    this.loadFilterCounts();
  }

  getPeers(pageNumber: number) {
    this.currentPage = pageNumber;
    this.isLoadingPeerReviewer = true;

    const request: any = {
      pageNumber: pageNumber,
      pageSize: this.pageSize
    };

    if (this.searchTerm && this.searchTerm.trim() !== '') {
      request.peerReviewerName = this.searchTerm;
    }

    if (this.selectedCountry && this.selectedCountry !== 'All Countries') {
      request.country = this.selectedCountry;
    }

    if (this.selectedBoard && this.selectedBoard !== 'All Boards') {
      request.highSchoolBoard = this.selectedBoard;
    }

    if (this.selectedTarget && this.selectedTarget !== 'All peers') {
      let level = '';
      if (this.selectedTarget.toLowerCase().includes('undergrad')) {
        level = 'Undergraduate';
      } else if (this.selectedTarget.toLowerCase().includes('grad') || this.selectedTarget.toLowerCase().includes('master')) {
        level = 'Graduate';
      } else if (this.selectedTarget.toLowerCase().includes('mba')) {
        level = 'MBA';
      } else if (this.selectedTarget.toLowerCase().includes('stem')) {
        level = 'STEM';
      }
      if (level) {
        request.levelOfStudy = level;
      }
    }

    this.peerReviewService.getAllPeerReviewers(request).subscribe({
      next: (response) => {
        if (response) {
          let resultList = response.result || [];

          // Populate cardDisplay details for dynamic tag/flag/university logic
          resultList = resultList.map((p: any) => ({
            ...p,
            cardDisplay: this.getCardDisplayData(p)
          }));
          this.peerReviewers = resultList;
          this.totalRecords = response.total;
        } else {
          this.peerReviewers = [];
          this.totalRecords = 0;
        }
        this.isLoadingPeerReviewer = false;
      },
      error: (err) => {
        this.isLoadingPeerReviewer = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching Peers' });
      }
    });
  }

  loadFilterCounts() {
    this.peerReviewService.getAllPeerReviewers({
      pageNumber: 1,
      pageSize: 500
    }).subscribe({
      next: response => {
        if (response && response.result) {
          this.allPeersForCounts = response.result;
        }
      }
    });
  }

  // Count Getters
  getCountryCount(countryName: string): number {
    if (countryName === 'All Countries') return this.allPeersForCounts.length;
    const search = countryName.toLowerCase();
    return this.allPeersForCounts.filter(p => 
      (p.country?.toLowerCase() === search) || 
      (p.underGraduateCollageCountry?.toLowerCase() === search) || 
      (p.graduateCollageCountry?.toLowerCase() === search)
    ).length;
  }

  getBoardCount(boardName: string): number {
    if (boardName === 'All Boards') return this.allPeersForCounts.length;
    return this.allPeersForCounts.filter(p => p.highSchoolBoard?.toLowerCase() === boardName.toLowerCase()).length;
  }

  getTargetCount(targetName: string): number {
    if (targetName === 'All peers') return this.allPeersForCounts.length;
    const lower = targetName.toLowerCase();
    if (lower.includes('undergrad')) {
      return this.allPeersForCounts.filter(p => p.underGraduateCollageName).length;
    } else if (lower.includes('grad') || lower.includes('master')) {
      return this.allPeersForCounts.filter(p => p.graduateCollageName).length;
    } else if (lower.includes('mba')) {
      return this.allPeersForCounts.filter(p => (p.graduateMajorOrDegree || '').toLowerCase().includes('mba')).length;
    } else if (lower.includes('stem')) {
      return this.allPeersForCounts.filter(p => 
        (p.underGraduateMajorOrDegree || '').toLowerCase().includes('stem') || 
        (p.graduateMajorOrDegree || '').toLowerCase().includes('stem')
      ).length;
    }
    return 0;
  }

  // Filter Selection Helpers
  setCountry(country: string) {
    this.selectedCountry = country;
    this.getPeers(1);
  }

  setBoard(board: string) {
    this.selectedBoard = board;
    this.getPeers(1);
  }

  setTarget(target: string) {
    this.selectedTarget = target;
    this.getPeers(1);
  }

  onSearchChange() {
    this.getPeers(1);
  }

  // Pagination getters
  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  get pages(): number[] {
    const list = [];
    for (let i = 1; i <= this.totalPages; i++) {
      list.push(i);
    }
    return list;
  }

  get combinedItems(): any[] {
    const list: any[] = [...this.peerReviewers];
    if (list.length >= 3) {
      list.splice(3, 0, { isCta: true });
    } else {
      list.push({ isCta: true });
    }
    return list;
  }

  // FAQ toggles
  toggleFaq(index: number) {
    this.activeFaqIndex = this.activeFaqIndex === index ? null : index;
  }

  schedulePeerCall(mentorId: string) {
    this.router.navigate(['/portal', 'schedule-call', mentorId]);
  }
}
